import { Button, cn, Textarea } from '@heroui/react'
import { useGenerateEntropy, useGenerateNoise } from '@qse/api'
import { ModelControlEnum, UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { ModelControl, PaymentOverdueBanner } from '@qse/ui'
import { AlertTriangle, CopyIcon, DownloadIcon } from 'lucide-react'
import React, { useRef, useState } from 'react'

import { useKeyProtectionContext } from '@/contexts/key-protection-context'
import { useKeyProtection } from '@/hooks'

const EntropySection = ({
  title,
  description,
  onGenerate,
  response,
  loading,
  onCopy,
  onDownload,
  copyLabel,
  errorMessage,
  isExceeded,
}: {
  title: string
  description: React.ReactNode
  onGenerate: () => void
  response: string
  loading: boolean
  onCopy: () => void
  onDownload: () => void
  copyLabel: string
  errorMessage?: string
  isExceeded?: boolean
}) => (
  <div className="dark:bg-zinc-900 bg-white rounded-lg p-6 mb-8 shadow w-full border dark:border-zinc-800">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    <p className="">{description}</p>
    <div className="my-4">
      <label className="block text-zinc-400 text-sm mb-1">View the Entropy Response</label>
      <div className="relative mt-2">
        <Textarea
          readOnly
          errorMessage={errorMessage}
          isInvalid={!!errorMessage}
          minRows={4}
          placeholder="Enter your description"
          radius="sm"
          value={loading ? 'Generating...' : response}
          variant="bordered"
          classNames={{
            label: [
              'text-foreground',
              'dark:text-foreground',
              'group-data-[filled-within=true]:text-white',
              'dark:group-data-[filled-within=true]:text-white',
              'group-data-[invalid=true]:text-danger',
              'dark:group-data-[invalid=true]:text-danger',
            ],
            input: [
              'bg-transparent',
              'text-gray-900 dark:text-white',
              'placeholder:text-gray-500 dark:placeholder:text-white/60 group-data-[invalid=true]:placeholder:text-danger',
            ],
            innerWrapper: 'bg-transparent',
            inputWrapper: [
              'text-gray-900 dark:!text-white',
              'bg-gray-50 dark:bg-[#1e1e20]',
              'border-gray-200 dark:border-default-600',
              'hover:bg-gray-100 dark:hover:bg-[#1e1e20]/70',
              'group-data-[focus=true]:bg-gray-100 dark:group-data-[focus=true]:bg-[#1e1e20]/50',
              'group-data-[focus=true]:border-primary dark:group-data-[focus=true]:border-primary',
              '!cursor-text',
            ],
          }}
        />
        {response && (
          <div className="absolute top-2 right-2 z-40 flex gap-2">
            <Button
              className=""
              disabled={!response || loading}
              radius="sm"
              size="sm"
              startContent={<CopyIcon className="w-[18px]" color="currentColor" size={18} />}
              onPress={onCopy}
            >
              {copyLabel}
            </Button>
            <Button
              className=""
              disabled={!response || loading}
              radius="sm"
              size="sm"
              startContent={<DownloadIcon className="w-[18px]" color="currentColor" size={18} />}
              onPress={onDownload}
            >
              Download
            </Button>
          </div>
        )}
      </div>
    </div>
    <div className="flex justify-end">
      <Button
        color="primary"
        isDisabled={loading || isExceeded}
        isLoading={loading}
        radius="sm"
        onPress={onGenerate}
      >
        Generate
      </Button>
    </div>
  </div>
)

const GenerateEntropyScreen = () => {
  const [copiedEntropy, setCopiedEntropy] = useState(false)
  const [copiedNoise, setCopiedNoise] = useState(false)

  const { user } = useUserStore()
  const isExceeded = user?.drive?.quotaSummary?.service.status === 'EXCEEDED'
  const entropyQuery = useGenerateEntropy()
  const noiseQuery = useGenerateNoise()
  const [openQuotaExceededModal, setOpenQuotaExceededModal] = useState(false)

  const { enableProtection: enableAppProtection, onKeysCopied: onAppKeysCopied } =
    useKeyProtectionContext()

  const {
    enableProtection: enableBrowserProtection,
    onKeysCopied: onBrowserKeysCopied,
    showKeyLossWarning,
  } = useKeyProtection({
    onBeforeUnload: () => {
      console.log('User left page with generated keys - quota will be deducted')
    },
    onProtectionDisabled: () => {
      console.log('Key protection disabled - keys were copied')
    },
  })

  // Responses are shown one time only, so track per section whether the current
  // one was copied/downloaded - regenerating replaces it and wastes its quota
  const entropySavedRef = useRef(false)
  const noiseSavedRef = useRef(false)

  const startGenerateEntropy = () => {
    setCopiedEntropy(false)
    entropySavedRef.current = false
    entropyQuery.refetch()
    enableAppProtection()
    enableBrowserProtection()
  }

  const handleGenerateEntropy = () => {
    if (isExceeded) {
      setOpenQuotaExceededModal(true)

      return
    }

    if (entropyQuery.data?.data && !entropySavedRef.current) {
      showKeyLossWarning({
        message:
          'The current generated key will be lost if you generate a new one. Are you sure you want to continue?',
        proceedButtonText: 'Generate New Key',
        onProceed: startGenerateEntropy,
      })

      return
    }

    startGenerateEntropy()
  }

  const startGenerateNoise = () => {
    setCopiedNoise(false)
    noiseSavedRef.current = false
    noiseQuery.refetch()
    enableAppProtection()
    enableBrowserProtection()
  }

  const handleGenerateNoise = () => {
    if (isExceeded) {
      setOpenQuotaExceededModal(true)

      return
    }

    if (noiseQuery.data?.data && !noiseSavedRef.current) {
      showKeyLossWarning({
        message:
          'The current generated key will be lost if you generate a new one. Are you sure you want to continue?',
        proceedButtonText: 'Generate New Key',
        onProceed: startGenerateNoise,
      })

      return
    }

    startGenerateNoise()
  }

  const handleCopyEntropy = () => {
    if (entropyQuery.data?.data) {
      navigator.clipboard.writeText(entropyQuery.data.data)
      setCopiedEntropy(true)
      setTimeout(() => setCopiedEntropy(false), 1200)
      entropySavedRef.current = true
      enableAppProtection()
      enableBrowserProtection()
      onAppKeysCopied()
      onBrowserKeysCopied()
    }
  }

  const handleCopyNoise = () => {
    if (noiseQuery.data?.data) {
      navigator.clipboard.writeText(noiseQuery.data.data)
      setCopiedNoise(true)
      setTimeout(() => setCopiedNoise(false), 1200)
      noiseSavedRef.current = true
      enableAppProtection()
      enableBrowserProtection()
      onAppKeysCopied()
      onBrowserKeysCopied()
    }
  }

  const handleDownloadEntropy = () => {
    if (entropyQuery.data?.data) {
      const blob = new Blob([entropyQuery.data.data], { type: 'application/x-pem-file' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = 'quantum-entropy-key.pem'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      entropySavedRef.current = true
      enableAppProtection()
      enableBrowserProtection()
      onAppKeysCopied()
      onBrowserKeysCopied()
    }
  }

  const handleDownloadNoise = () => {
    if (noiseQuery.data?.data) {
      const blob = new Blob([noiseQuery.data.data], { type: 'application/x-pem-file' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = 'quantum-noise-key.pem'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      noiseSavedRef.current = true
      enableAppProtection()
      enableBrowserProtection()
      onAppKeysCopied()
      onBrowserKeysCopied()
    }
  }
  const isTenantAdmin = user?.userRole === UserRoleEnum.TENANT_ADMIN

  return (
    <div className="lg:pb-4 pb-32 flex flex-col w-full md:px-[30px] px-4">
      {isTenantAdmin && (
        <div className=" sticky md:top-24 top-20 w-full h-full pb-1 bg-black z-50">
          <PaymentOverdueBanner isInView={false} />
        </div>
      )}
      <div className="w-full">
        <h1 className="text-2xl font-bold mb-2">Quantum Entropy as a Service</h1>
        <p className="mb-8 text-sm">
          The first and only real-time quantum resilient entropy sourcing system!
        </p>

        {/* Model Control */}
        <ModelControl
          control={ModelControlEnum.QUOTA_EXCEEDED}
          data={{}}
          reset={() => {}}
          setModelActive={() => {}}
          setToggleModel={setOpenQuotaExceededModal}
          toggleModel={openQuotaExceededModal}
          onClose={() => setOpenQuotaExceededModal(false)}
        />
        {/* Loading indicator with progress message */}
        {isExceeded ? (
          <div
            className={cn(
              'border border-red-200 mb-8 bg-red-500/10 dark:border-red-800 rounded-lg shadow-sm transform transition-all duration-300 ease-in-out',
              'translate-y-0 opacity-100',
            )}
          >
            <div className="px-4 py-3">
              <div className="flex flex-row gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-500 dark:text-[#DD524C]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-900 dark:text-red-100 ">
                      User quota exceeded - key generation is disabled until quota resets
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          ''
        )}
        <EntropySection
          copyLabel={copiedEntropy ? 'Copied!' : 'Copy'}
          errorMessage={entropyQuery.error?.message}
          isExceeded={isExceeded}
          loading={entropyQuery.isLoading}
          response={entropyQuery.data?.data || ''}
          title="Generate Quantum Entropy Request"
          description={
            <div className="flex flex-col gap-3">
              <p>
                Quantum Entropy protects established key-generation algorithms from quantum
                computers. The platform provides easy API-access to the consumer, allowing for
                entropy to seamlessly be injected into the key-generation ceremony.
              </p>
              <p>
                Our generators meet all requirements of NIST SP 800-90A, 90B and 90C (draft)
                standards for NonDeterministic Random Bit Generators (while satisfying NIST SP
                800-22 (NIST STS) and Dieharder tests).
              </p>
            </div>
          }
          onCopy={handleCopyEntropy}
          onDownload={handleDownloadEntropy}
          onGenerate={handleGenerateEntropy}
        />
        <EntropySection
          copyLabel={copiedNoise ? 'Copied!' : 'Copy'}
          errorMessage={noiseQuery.error?.message}
          isExceeded={isExceeded}
          loading={noiseQuery.isLoading}
          response={noiseQuery.data?.data || ''}
          title="Generate Quantum Noise Proof Request"
          description={
            <div className="flex flex-col gap-3">
              <p>
                Our proprietary SaaS infrastructure uniquely allows customers to sample and validate
                the strength and quality of the delivered non-deterministic quantum entropy and test
                it against industry-standard validation suites.
              </p>
            </div>
          }
          onCopy={handleCopyNoise}
          onDownload={handleDownloadNoise}
          onGenerate={handleGenerateNoise}
        />
      </div>
    </div>
  )
}

export default GenerateEntropyScreen
